import { useSelector } from "react-redux";
import { RootState } from "../../store";
import React, { useEffect, useRef, useState } from "react";
import { DebitNote } from "../../models/debit-note";
import {
  ItemRequestStatus,
  createItem,
  getItem,
  updateItem,
} from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import {
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
} from "@coreui/react";
import { useHistory } from "react-router-dom";
import { errorAlert, successAlert } from "../utils/messages";
import Loading from "../indicators/Loading";
import ClientUpdateModal from "../client/ClientUpdateModal";
import { Spinner } from "react-bootstrap";
import { getRoutePath } from "../../routes";
import NotFound from "../indicators/NotFound";
import ServerError from "../indicators/ServerError";
import { getClientForDocument } from "../../api/client";
import { getStampById } from "../../api/stamp";
import { getEstablishmentById } from "../../api/establishment";
import { getDispatchPointById } from "../../api/dispatch-point";
import { getInvoiceById } from "../../api/invoice";
import { changeTZToPyg, changeTZToUtc } from "../../utils/dates";
import Client from "../../models/client";
import { PYG } from "../../currency/available-currencies";
import { useIdempotencyKey } from "../../hooks/use-idempotency-key";

// Context provider
import {
  DebitNoteFormProvider,
  useDebitNoteFormContext,
} from "./context/DebitNoteFormContext";

// Section components
import { DebitNoteFormHeader } from "./sections/DebitNoteFormHeader";
import { DebitNoteFormDNCPSection } from "./sections/DebitNoteFormDNCPSection";
import { DebitNoteFormDetailsSection } from "./sections/DebitNoteFormDetailsSection";
import DebitNoteFormAssociatedDocumentSection from "./sections/DebitNoteFormAssociatedDocumentSection";
import { DebitNoteFormTotals } from "./sections/DebitNoteFormTotals";
import { DebitNoteFormItems } from "./sections/DebitNoteFormItems";

interface DebitNoteFormProps {
  readonly?: boolean;
}

// Inner component that uses the context
const DebitNoteFormContent: React.FC<{
  readonly?: boolean;
}> = ({ readonly }) => {
  const [showAddClient, setShowAddClient] = useState(false);
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );
  const establishments = useSelector(
    (state: RootState) => state.taxPayerParameters.data.establishments
  );
  const dispatchPoints = useSelector(
    (state: RootState) => state.taxPayerParameters.data.dispatchPoints
  );
  const stamps = useSelector(
    (state: RootState) => state.taxPayerParameters.data.stamps
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const submissionLockRef = useRef(false);
  const idempotencyKey = useIdempotencyKey();
  const [loadError, setLoadError] = useState(false);
  const [loadNotFound, setLoadNotFound] = useState(false);

  const history = useHistory();

  const {
    debitNoteRef,
    setDebitNoteDate,
    setStamp,
    setEstablishment,
    setDispatchPoint,
    setClient,
    setCurrency,
    setPygExchangeRate,
    setAssociatedDocumentType,
    setElectronicInvoice,
    onItemsChange,
    setErrors,
  } = useDebitNoteFormContext();

  const getEditingDebitNoteId = () => {
    const urlParams = new URLSearchParams(history.location.search);
    const editingDebitNoteId = urlParams.get("id");

    if (editingDebitNoteId !== null) {
      if (!isNaN(parseInt(editingDebitNoteId))) {
        return parseInt(editingDebitNoteId);
      }
    }

    return undefined;
  };

  // Auto-select stamp, establishment and dispatch point if only one available
  useEffect(() => {
    const editingDebitNoteId = getEditingDebitNoteId();
    if (editingDebitNoteId !== undefined) {
      return;
    }

    if (stamps.length === 1 && debitNoteRef.current?.stampId === undefined) {
      const stamp = stamps[0];
      if (debitNoteRef.current) {
        debitNoteRef.current.stampId = stamp.id;
        debitNoteRef.current.stampIdentifier = stamp.identifier;
        debitNoteRef.current.stampBeginDate = stamp.beginDate;
      }
      setStamp(stamp);
    }

    if (
      establishments.length === 1 &&
      debitNoteRef.current?.establishmentId === undefined
    ) {
      const establishment = establishments[0];
      if (debitNoteRef.current) {
        debitNoteRef.current.establishmentId = establishment.id;
        debitNoteRef.current.establishmentCode = establishment.code;
      }
      setEstablishment(establishment);
    }

    if (
      dispatchPoints.length === 1 &&
      debitNoteRef.current?.dispatchPointId === undefined
    ) {
      const dispatchPoint = dispatchPoints[0];
      if (debitNoteRef.current) {
        debitNoteRef.current.dispatchPointId = dispatchPoint.id;
        debitNoteRef.current.dispatchPointIdentifier = dispatchPoint.identifier;
      }
      setDispatchPoint(dispatchPoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamps, establishments, dispatchPoints]);

  const loadInitialDebitNoteById = async (): Promise<DebitNote | null> => {
    const editingDebitNoteId = getEditingDebitNoteId();

    if (editingDebitNoteId === undefined) {
      return null;
    }

    let requestStatus = await getItem<DebitNote>(
      `/debit_notes/${editingDebitNoteId}/`
    );

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.status === NOT_FOUND) {
        setLoadNotFound(true);
      } else {
        setLoadError(true);
      }

      return null;
    }

    if (requestStatus.data === undefined || requestStatus.data === null) {
      setLoadError(true);
      return null;
    }

    return requestStatus.data!;
  };

  const loadInitialData = async () => {
    setLoading(true);
    const debitNote = await loadInitialDebitNoteById();

    if (debitNote === null) {
      setLoading(false);
      return;
    }

    const stampPromise = getStampById(debitNote.stampId, debitNote.taxPayerId);
    const establishmentPromise = getEstablishmentById(
      debitNote.establishmentId,
      debitNote.taxPayerId
    );
    const dispatchPointPromise = getDispatchPointById(
      debitNote.dispatchPointId,
      debitNote.taxPayerId
    );
    const clientPromise = getClientForDocument(debitNote);

    let invoicePromise = null;
    if (debitNote.invoiceId !== undefined && debitNote.invoiceId !== null) {
      invoicePromise = getInvoiceById(
        debitNote.invoiceId,
        debitNote.taxPayerId
      );
    }

    const stamp = await stampPromise;
    const establishment = await establishmentPromise;
    const dispatchPoint = await dispatchPointPromise;
    const client = await clientPromise;
    const invoice = invoicePromise !== null ? await invoicePromise : null;

    debitNoteRef.current = debitNote;

    const pygDate = new Date(debitNote.debitNoteDate!);
    const year = pygDate.getUTCFullYear();
    const month = pygDate.getUTCMonth();
    const day = pygDate.getUTCDate();
    const hours = pygDate.getUTCHours();
    const minutes = pygDate.getUTCMinutes();
    const seconds = pygDate.getUTCSeconds();
    const debitNoteDate = new Date(year, month, day, hours, minutes, seconds);
    setDebitNoteDate(debitNoteDate);
    setStamp(stamp);
    setEstablishment(establishment);
    setDispatchPoint(dispatchPoint);
    setClient(client);
    setAssociatedDocumentType(debitNote.associatedDocumentType);
    setElectronicInvoice(invoice);
    onItemsChange(debitNote.items);

    setCurrency(debitNote.currency ? debitNote.currency : PYG);
    setPygExchangeRate(debitNote.pygExchangeRate);

    setLoading(false);
  };

  useEffect(() => {
    const newDebitNoteDate = changeTZToPyg(new Date());
    if (debitNoteRef.current !== null) {
      debitNoteRef.current.debitNoteDate =
        changeTZToUtc(newDebitNoteDate).toISOString();
      setDebitNoteDate(newDebitNoteDate);
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddClientSuccess = (newClient?: Client) => {
    let message = "Cliente agregado correctamente";
    if (newClient) {
      message = `Cliente ${newClient.socialName} agregado correctamente`;
      if (debitNoteRef.current) {
        debitNoteRef.current.clientId = newClient.id;
        debitNoteRef.current.clientCode = newClient.code;
        debitNoteRef.current.clientIsTaxPayer = newClient.isTaxPayer;
        debitNoteRef.current.clientRuc = newClient.ruc;
        debitNoteRef.current.clientSocialName = newClient.socialName;
        debitNoteRef.current.clientFantasyName = newClient.fantasyName;
        debitNoteRef.current.clientType = newClient.type;
        debitNoteRef.current.clientCountryCode = newClient.countryCode;
        debitNoteRef.current.clientHasAddress = newClient.hasAddress;
        debitNoteRef.current.clientAddress = newClient.address;
        debitNoteRef.current.clientHouseNumber = newClient.houseNumber;
        debitNoteRef.current.clientDepartment = newClient.department;
        debitNoteRef.current.clientDepartmentDescription =
          newClient.departmentDescription;
        debitNoteRef.current.clientDistrict = newClient.district;
        debitNoteRef.current.clientDistrictDescription =
          newClient.districtDescription;
        debitNoteRef.current.clientCity = newClient.city;
        debitNoteRef.current.clientCityDescription = newClient.cityDescription;
        debitNoteRef.current.clientPhoneNumber = newClient.phoneNumber;
        debitNoteRef.current.clientEmail = newClient.email;
      }
      setClient(newClient);
    }
    successAlert(message);
    setShowAddClient(false);
  };

  const onSave = async () => {
    if (debitNoteRef.current === null || submissionLockRef.current) {
      return;
    }

    submissionLockRef.current = true;
    setSubmitting(true);

    let requestPromise: Promise<ItemRequestStatus<DebitNote>>;
    let successMessage = "Nota de Débito agregada exitósamente";

    const debitNote = debitNoteRef.current;
    debitNote.taxPayerRuc = taxPayer.ruc;
    debitNote.taxPayerSocialName = taxPayer.socialName;
    debitNote.taxPayerFantasyName = taxPayer.fantasyName;

    if (debitNote.currency === undefined) {
      debitNote.currency = PYG;
    }

    if (debitNote.id === undefined) {
      requestPromise = createItem<DebitNote>("/debit_notes/create/", debitNote, {
        "Idempotency-Key": idempotencyKey,
      });
    } else {
      requestPromise = updateItem<DebitNote>(
        `/debit_notes/${debitNote.id}/`,
        debitNote
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";

      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
      submissionLockRef.current = false;
    } else {
      successAlert(successMessage);
      history.push(getRoutePath("debitNotes"));
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (loadNotFound) {
    return <NotFound />;
  }

  if (loadError) {
    return <ServerError />;
  }

  return (
    <>
      <CCard className="shadow-sm border-0">
        <CCardHeader
          className="d-flex align-items-center"
          style={{
            background: "linear-gradient(180deg, #006657 0%, #095e55 100%)",
            color: "white",
            borderBottom: "none",
            padding: "14px 20px",
          }}
        >
          <i className="fa fa-file-text-o mr-2" style={{ fontSize: "1.1rem" }}></i>
          <span style={{ fontWeight: 600, fontSize: "1rem" }}>
            {debitNoteRef.current !== null &&
            debitNoteRef.current.id !== undefined ? (
              <>
                {readonly ? "Ver" : "Editar"} Nota de Débito Electrónica{" "}
                <span style={{ opacity: 0.85 }}>#{debitNoteRef.current.id}</span>
              </>
            ) : (
              <>Agregar Nota de Débito Electrónica</>
            )}
          </span>
        </CCardHeader>
        <CCardBody style={{ padding: "24px" }}>
          <fieldset disabled={readonly}>
            <DebitNoteFormHeader
              onAddClientClick={() => setShowAddClient(true)}
            />
            <DebitNoteFormDNCPSection />
            <DebitNoteFormAssociatedDocumentSection />
            <DebitNoteFormDetailsSection />
            <DebitNoteFormTotals />
            <DebitNoteFormItems />
          </fieldset>
        </CCardBody>
        <CCardFooter
          style={{
            background: "var(--theme-surface-raised)",
            borderTop: "1px solid var(--theme-divider)",
            padding: "14px 20px",
          }}
        >
          {readonly !== true ? (
            <CButton
              type="submit"
              color="primary"
              onClick={onSave}
              disabled={submitting}
              style={{ minWidth: "130px", fontWeight: 600, padding: "8px 20px" }}
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <i className="fa fa-save mr-2"></i>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          ) : (
            <></>
          )}
        </CCardFooter>
      </CCard>
      <ClientUpdateModal
        show={showAddClient}
        onSuccess={onAddClientSuccess}
        onCancel={() => setShowAddClient(false)}
      />
    </>
  );
};

// Main wrapper component
const DebitNoteForm: React.FC<DebitNoteFormProps> = ({ readonly }) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );

  return (
    <DebitNoteFormProvider taxPayerId={taxPayer.id} readonly={readonly}>
      <DebitNoteFormContent readonly={readonly} />
    </DebitNoteFormProvider>
  );
};

export default DebitNoteForm;
